import type { AnyAction } from 'redux';
import { delay, put, race, take } from 'redux-saga/effects';
import { Subject } from 'rxjs';

import { isNonNullable } from '@pressreader/types';

import type { ActionCreator, IAction } from './models/action';
import type { GeneratorResult } from './models/generator.result';
import { dispatch, store, storeManager } from './store.manager.instance';

export { baseApi } from './base.api';
export { enableSharedRtk } from './enable.rtk';
export type { IdentityClock, IdentityCommitPredicate } from './identity.clock';
export { setIdentityClock, setIdentityCommitPredicate } from './identity.clock';
export type { IdentityPolicy } from './identity.policy';
export { armIdentityCommitReconcile, declareIdentityPolicy, runIdentityCommitReconcile } from './identity.policy';
export type { IdentityChangedAbort, IdentityChangedError, IdentityQueryApi, QueryIdentity } from './identity.scope';
export { IDENTITY_CHANGED, identityScoped, isIdentityChangedError } from './identity.scope';
export { currentIdentityGeneration, isCurrentGeneration } from './identity.snapshot';
export type { AppListenerEffectApi, AppThunkDispatch } from './listener.middleware';
export { listenerMiddleware, listenerOnError, startAppListening } from './listener.middleware';
export * from './query.fn';
// `classifyRead` stays lib-internal on purpose — the exported entry points are the two that own the
// subscription release (`readQuery` here, `runQuery` in ./sagautils).
export type { QueryReadResult } from './query.read';
export { readQuery } from './query.read';
export { createValueStableProjection } from './value.stable.projection';

export { dispatch, store, storeManager };
export const select = <S = unknown, R = unknown>(selector: (state: S) => R) => selector(store.getState());
/**
 * Apply a selector to an externally supplied state — e.g. the `getState()` an RTK Query `queryFn` or a
 * listener effect is handed (typed `unknown` on the store-agnostic api). Keeps the unknown→state cast in
 * one place so call sites stay cast-free and read the injected store rather than the singleton.
 */
export const selectFrom = <S = unknown, R = unknown>(state: unknown, selector: (state: S) => R): R => selector(state as S);
/** Be careful using it: store subscription remains even if there are no more observable subscibers. */
export function observeSelector<S = unknown, R = unknown>(selector: (state: S) => R) {
    const subject = new Subject<R>();

    let prevValue = select(selector);

    store.subscribe(() => {
        const newValue = select(selector);

        if (prevValue !== newValue) {
            prevValue = newValue;
            subject.next(newValue);
        }
    });

    return subject.asObservable();
}

/**
 * Run a saga as a detached root task and expose it as a promise: resolves with the saga's return
 * value, rejects if the saga errors. The standard bridge for async facades over unchanged sagas
 * (`openPopupAsync`, `loginFlowAsync`) — keeps reject-on-error semantics in one place. Note the task
 * is owned by the saga middleware, not the caller: cancelling/abandoning the awaiting code does not
 * cancel the saga.
 */
export function runSagaAsPromise<R>(saga: () => GeneratorResult<R>): Promise<R> {
    return storeManager.runSaga(saga).toPromise();
}

export function waitForAction(actionName: string, timeout = 1000) {
    return new Promise<void>(resolve => {
        storeManager.runSaga(function* () {
            yield race({
                wait: take(actionName),
                timeout: delay(timeout),
            });

            resolve();
        });
    });
}

export function waitForActionResult<TPayload>(action: ActionCreator<TPayload>, timeout = 1000) {
    return new Promise<TPayload | null>(resolve => {
        storeManager.runSaga(function* () {
            const result: { wait?: IAction<TPayload>; timeout?: true } = yield race({
                wait: take(`${action}`),
                timeout: delay(timeout),
            });
            resolve(result.wait?.payload ?? null);
        });
    });
}

export function runAction<TResult, TError>(
    action: AnyAction,
    doneAction: ActionCreator<TResult>,
    failedAction?: ActionCreator<TError>,
    ...otherActions: ActionCreator<any>[]
) {
    return new Promise<TResult>((resolve, reject) => {
        storeManager.runSaga(function* () {
            yield put(action);
            const result: AnyAction = yield take([doneAction, failedAction, ...otherActions].filter(isNonNullable));
            if (result.type === `${doneAction}`) {
                resolve((result as ReturnType<typeof doneAction>).payload);
            } else {
                reject((result as IAction<unknown>).payload);
            }
        });
    });
}

export function subscribe<S = unknown, R = unknown>(selector: (state: S) => R, listener: (next: R) => void) {
    let prevValue = select(selector);

    return store.subscribe(() => {
        const newValue = select(selector);

        if (prevValue !== newValue) {
            prevValue = newValue;
            listener(newValue);
        }
    });
}

export function createParameterSelector<P extends string, R>(paramName: P) {
    return function parameterSelector(_: unknown, param: { [K in P]: R }) {
        return param[paramName];
    };
}

function createOptionalParameterSelector<P extends string, R extends string | number | boolean | Date>(
    paramName: P,
): (_: unknown, param: { [K in P]?: R }) => R | undefined;
function createOptionalParameterSelector<P extends string, R extends string | number | boolean | Date>(
    paramName: P,
    defaultValue: R,
): (_: unknown, param: { [K in P]?: R }) => R;
function createOptionalParameterSelector<P extends string, R extends string | number | boolean | Date>(paramName: P, defaultValue?: R) {
    return (_: unknown, param: { [K in P]?: R }) => param[paramName] ?? defaultValue;
}
export { createOptionalParameterSelector };
